/**
 * @engine/vfx - Operator Nodes
 *
 * Math, random, and utility nodes available in any context.
 */

import type { VFXNodeDefinition } from '../../types';
import { dataPort } from '../../types';

/**
 * Operator node definitions.
 */

export const CONVERSION_NODES: readonly VFXNodeDefinition[] = [

  {
    id: 'vfx.operator.float_to_uint',
    name: 'Float to UInt',
    category: 'operator',
    contexts: [],
    description: 'Convert float to unsigned integer',
    color: '#9E9E9E',
    inputs: [
      dataPort('x', 'X', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'uint'),
    ],
    shader: {
      code: `result = uint(max(x, 0.0));`,
    },
  },

  {
    id: 'vfx.operator.uint_to_float',
    name: 'UInt to Float',
    category: 'operator',
    contexts: [],
    description: 'Convert unsigned integer to float',
    color: '#9E9E9E',
    inputs: [
      dataPort('x', 'X', 'uint', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = float(x);`,
    },
  },

  {
    id: 'vfx.operator.vec2_to_vec3',
    name: 'Vec2 to Vec3',
    category: 'operator',
    contexts: [],
    description: 'Convert vec2 to vec3 (z=0)',
    color: '#9E9E9E',
    inputs: [
      dataPort('v', 'Vector', 'vec2', { defaultValue: { x: 0, y: 0 } }),
      dataPort('z', 'Z', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'vec3'),
    ],
    shader: {
      code: `result = float3(v.x, v.y, z);`,
    },
  },

  {
    id: 'vfx.operator.vec3_to_vec2',
    name: 'Vec3 to Vec2',
    category: 'operator',
    contexts: [],
    description: 'Convert vec3 to vec2 (drop z)',
    color: '#9E9E9E',
    inputs: [
      dataPort('v', 'Vector', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
    ],
    outputs: [
      dataPort('result', 'Result', 'vec2'),
    ],
    shader: {
      code: `result = float2(v.x, v.y);`,
    },
  },

  {
    id: 'vfx.operator.vec2_compose',
    name: 'Compose Vec2',
    category: 'operator',
    contexts: [],
    description: 'Create vec2 from components',
    color: '#607D8B',
    inputs: [
      dataPort('x', 'X', 'float', { defaultValue: 0 }),
      dataPort('y', 'Y', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'vec2'),
    ],
    shader: {
      code: `result = float2(x, y);`,
    },
  },

  {
    id: 'vfx.operator.vec2_decompose',
    name: 'Decompose Vec2',
    category: 'operator',
    contexts: [],
    description: 'Extract components from vec2',
    color: '#607D8B',
    inputs: [
      dataPort('v', 'Vector', 'vec2', { defaultValue: { x: 0, y: 0 } }),
    ],
    outputs: [
      dataPort('x', 'X', 'float'),
      dataPort('y', 'Y', 'float'),
    ],
    shader: {
      code: `x = v.x; y = v.y;`,
    },
  },
];
